import {
  GoogleFontFamily,
  type WebsiteColors,
  type WebsiteDecorationStyle,
  type WebsiteStyles,
} from '../website_types/Styles';
import { SECTORAL_DEFAULTS, type OnboardingSector } from './sectoral-defaults';

/**
 * Thème builder (couleurs + typographies + style de bordure) associé à chaque
 * palette sectorielle (`SectoralDefault.palette`). Utilisé par l'orchestrateur de
 * génération du site (Lot 2) pour donner d'emblée une identité visuelle cohérente
 * avec le métier — le pro ajuste ensuite librement dans le builder.
 *
 * Clé = identifiant `palette_*` déclaré dans `SECTORAL_DEFAULTS`.
 */
export const SECTORAL_PALETTE_THEME: Record<string, WebsiteStyles> = {
  palette_beaute: {
    colors: { background: '#fdf7f4', accent: '#b76e79', text: '#2a2024', cta: '#c98a94' },
    typography: { body: GoogleFontFamily.Lato, heading: GoogleFontFamily.Playfair },
    borderStyle: 'soft',
  },
  palette_bien_etre: {
    colors: { background: '#f5f8f4', accent: '#6b8e6f', text: '#22281f', cta: '#7fae84' },
    typography: { body: GoogleFontFamily.Nunito, heading: GoogleFontFamily.Merriweather },
    borderStyle: 'rounded',
  },
  palette_vtc: {
    colors: { background: '#0f1115', accent: '#c9a14a', text: '#f2f2f2', cta: '#d8b35c' },
    typography: { body: GoogleFontFamily.Montserrat, heading: GoogleFontFamily.Montserrat },
    borderStyle: 'none',
  },
  palette_coach_sport: {
    colors: { background: '#101418', accent: '#e0533d', text: '#f4f4f4', cta: '#ef6a55' },
    typography: { body: GoogleFontFamily.Poppins, heading: GoogleFontFamily.Poppins },
    borderStyle: 'soft',
  },
  palette_restauration: {
    colors: { background: '#fbf6ee', accent: '#9c3b2e', text: '#2b211b', cta: '#b8493a' },
    typography: { body: GoogleFontFamily.SourceSansPro, heading: GoogleFontFamily.Playfair },
    borderStyle: 'soft',
  },
  palette_artisan: {
    colors: { background: '#f6f3ee', accent: '#8a6d3b', text: '#272219', cta: '#a3824a' },
    typography: { body: GoogleFontFamily.Roboto, heading: GoogleFontFamily.Merriweather },
    borderStyle: 'none',
  },
  palette_medical: {
    colors: { background: '#f4f8fb', accent: '#2f7ca6', text: '#1c2730', cta: '#3a93c2' },
    typography: { body: GoogleFontFamily.OpenSans, heading: GoogleFontFamily.OpenSans },
    borderStyle: 'rounded',
  },
  palette_photographe: {
    colors: { background: '#ffffff', accent: '#1a1a1a', text: '#1a1a1a', cta: '#1a1a1a' },
    typography: { body: GoogleFontFamily.Raleway, heading: GoogleFontFamily.Raleway },
    borderStyle: 'none',
  },
  palette_conseil: {
    colors: { background: '#f7f8fa', accent: '#2d4a6b', text: '#1c242e', cta: '#365d86' },
    typography: { body: GoogleFontFamily.Inter, heading: GoogleFontFamily.Montserrat },
    borderStyle: 'soft',
  },
  palette_generic: {
    colors: { background: '#faf9f7', accent: '#2d2a4a', text: '#1e1e2f', cta: '#6c5ce7' },
    typography: { body: GoogleFontFamily.Inter, heading: GoogleFontFamily.Playfair },
    borderStyle: 'soft',
  },
  // ── Palettes ALTERNATIVES (switcher de styles preview, Lot 4 waouh v2) ─────────────
  // Additives : profitent aussi au builder/onboarding. Ne pas renommer les clés (persistées
  // dans website_preview_data.style_key des leads).
  palette_sombre_chic: {
    colors: { background: '#14100f', accent: '#d4b483', text: '#f4efe9', cta: '#c99a5b' },
    typography: { body: GoogleFontFamily.Lato, heading: GoogleFontFamily.Playfair },
    borderStyle: 'none',
  },
  palette_pastel_lumineux: {
    colors: { background: '#f9f6fb', accent: '#9b8bc4', text: '#3a3344', cta: '#b49ddb' },
    typography: { body: GoogleFontFamily.Nunito, heading: GoogleFontFamily.Raleway },
    borderStyle: 'rounded',
  },
  palette_naturel_sable: {
    colors: { background: '#f8f3ec', accent: '#b0764f', text: '#33291f', cta: '#c98a5e' },
    typography: { body: GoogleFontFamily.SourceSansPro, heading: GoogleFontFamily.Merriweather },
    borderStyle: 'soft',
  },
};

/**
 * Libellés FR des palettes (pastilles du switcher de styles preview). Data produit (comme
 * les banques sectorielles), pas une chaîne de traduction applicative.
 */
export const SECTORAL_PALETTE_LABELS: Record<string, string> = {
  palette_beaute: 'Élégance rosée',
  palette_bien_etre: 'Nature apaisante',
  palette_vtc: 'Nuit dorée',
  palette_coach_sport: 'Énergie sombre',
  palette_restauration: 'Terracotta gourmand',
  palette_artisan: 'Atelier boisé',
  palette_medical: 'Bleu serein',
  palette_photographe: 'Minimal contrasté',
  palette_conseil: 'Bleu profond',
  palette_generic: 'Signature indigo',
  palette_sombre_chic: 'Élégant sombre',
  palette_pastel_lumineux: 'Pastel lumineux',
  palette_naturel_sable: 'Naturel sable',
};

/**
 * Deux palettes ALTERNATIVES compatibles par secteur (switcher preview Lot 4) — la palette
 * par défaut du secteur reste la première proposition, celles-ci sont les propositions 2 et 3.
 */
export const SECTOR_ALT_PALETTES: Record<OnboardingSector, [string, string]> = {
  beaute: ['palette_sombre_chic', 'palette_pastel_lumineux'],
  bien_etre: ['palette_naturel_sable', 'palette_pastel_lumineux'],
  vtc: ['palette_photographe', 'palette_conseil'],
  coach_sport: ['palette_vtc', 'palette_photographe'],
  restauration: ['palette_naturel_sable', 'palette_sombre_chic'],
  artisan: ['palette_naturel_sable', 'palette_conseil'],
  medical: ['palette_pastel_lumineux', 'palette_photographe'],
  photographe: ['palette_sombre_chic', 'palette_conseil'],
  conseil: ['palette_photographe', 'palette_pastel_lumineux'],
  generic: ['palette_pastel_lumineux', 'palette_naturel_sable'],
};

/**
 * Décor de fond associé à chaque palette (switcher de styles preview). C'est ce qui fait
 * qu'un changement de style change VRAIMENT d'ambiance et pas seulement de couleurs : le
 * motif des gouttières suit la palette, au même titre que la typo et les bordures.
 *
 * Volontairement SÉPARÉ de `SECTORAL_PALETTE_THEME` : ce dernier alimente aussi la
 * génération du site réel à l'onboarding, qui décide de son décor par secteur — on ne veut
 * pas changer ce comportement en douce depuis ici.
 */
export const PALETTE_DECORATION: Record<string, WebsiteDecorationStyle> = {
  palette_beaute: 'curves',
  palette_bien_etre: 'waves',
  palette_vtc: 'none',
  palette_coach_sport: 'lines',
  palette_restauration: 'dots',
  palette_artisan: 'crosses',
  palette_medical: 'dots',
  palette_photographe: 'none',
  palette_conseil: 'lines',
  palette_generic: 'dots',
  palette_sombre_chic: 'diamonds',
  palette_pastel_lumineux: 'dots',
  palette_naturel_sable: 'waves',
};

/**
 * Thème builder pour un secteur donné, avec repli sur le thème générique si la
 * palette n'a pas de mapping (garantit toujours un `WebsiteStyles` valide).
 */
export const getSectoralPaletteTheme = (sector: OnboardingSector | null | undefined): WebsiteStyles => {
  // `sector` peut venir d'une colonne `category.sector` en texte libre (VarChar) :
  // une valeur non-null mais hors `OnboardingSector` n'aurait pas d'entrée dans
  // `SECTORAL_DEFAULTS` → repli explicite sur `generic` (sinon `.palette` throw).
  const def = SECTORAL_DEFAULTS[sector ?? 'generic'] ?? SECTORAL_DEFAULTS.generic;
  return SECTORAL_PALETTE_THEME[def.palette] ?? SECTORAL_PALETTE_THEME.palette_generic;
};

/** Couleurs du tunnel de réservation public (`/book`, `/gift-card`, widget) : accent, fond, texte. */
export interface TunnelAppearanceColors {
  accent: string;
  background: string;
  text: string;
}

/**
 * Apparence par défaut du tunnel de réservation quand le pro n'en a pas choisi
 * (`pro_booking_params.tunnel_*` vides) : les couleurs de SON site (le bouton du site devient
 * l'accent du tunnel : même mapping accent ⇄ cta que le sélecteur de couleurs du partage), sinon
 * la palette de son métier. Le client retrouve l'univers du site au lieu d'un bleu générique.
 */
export const defaultTunnelAppearance = (
  siteColors: WebsiteColors | null | undefined,
  sector: OnboardingSector | null | undefined,
): TunnelAppearanceColors => {
  const colors = siteColors ?? getSectoralPaletteTheme(sector).colors;
  return { accent: colors.cta, background: colors.background, text: colors.text };
};
